﻿import { store, store$ } from '@redux/store';
import { selectPaginationContext } from 'features/searchResultsFacets';
import { distinctUntilChanged, filter, map } from 'rxjs';
import { routingSelectors } from 'features/routing';
import Pager from './pager';
import PaginationNumberLink from './paginationNumberLink';

const { urlComponentsSelector } = routingSelectors;

/**
 * This class is ONLY in charge of updates the pagination UI according to the current search parameters.
 */
class PaginationBar {
  constructor() {
    const container = document.querySelector('[data-pagination]');
    const initialData = container.querySelector('[data-compontent="pagination-data"]');
    this.container = container;
    this.previousButton = container.querySelector('[data-previous]');
    this.nextButton = container.querySelector('[data-next]');
    this.totalResults = JSON.parse(initialData.innerHTML)?.TotalResults ?? 1;
    this.optionList = container.querySelector('[data-option-list]');
    this.pageCount = parseInt(this.optionList.dataset.pageCount, 10);
    this.maximumPagesToShow = 7;

    store$
      .pipe(
        map(selectPaginationContext),
        filter((parameters) => parameters.listingCount !== undefined),
        distinctUntilChanged((previous, current) => JSON.stringify(previous) === JSON.stringify(current))
      )
      .subscribe(this.changePagination.bind(this));

    (() => new Pager())();
  }

  /**
   * changePagination: Re build the pagination with the new data.
   * @param {Object} configuration Object with the data to update the component.
   */
  changePagination(parameters) {
    const { listingCount, pageSize, pageNumber } = parameters;
    this.totalResults = listingCount ?? this.totalResults;
    this.pageSize = pageSize ?? this.pageSize;
    this.activePageNumber = pageNumber ?? 1;
    this.updatePageCount();
    this.updateUrlComponents();
    this.getPagesToShow();
    this.build();
    this.updatePaginationBarVisibility();
    this.updateClasses();
  }

  /**
   * The function updates the URL components based on the current state of the store.
   */
  updateUrlComponents() {
    const state = store.getState();
    const urlComponents = urlComponentsSelector(state);

    this.urlComponents = urlComponents;
  }

  /**
   * updateCount: Updates the count with the actual data.
   */
  updatePageCount() {
    this.pageCount = Math.ceil(this.totalResults / this.pageSize);
  }

  /**
   * updateClasses: Updates the classes required for all the page HTML elements.
   */
  updateClasses() {
    const allOptions = this.optionList.querySelectorAll('[data-page]');
    allOptions.forEach((optionElement) => optionElement.classList.remove('active'));
    const activeOption = this.optionList.querySelector(`[data-page="${this.activePageNumber}"]`);
    if (activeOption) {
        activeOption.classList.add('nhs-c-pagination__number--active');
        activeOption.dataset.qa = "pagination_number_active";
    }
  }

  /**
   * Determines which pages to display based on the active page number and the total number of pages.
   * Sets the value of `this.pagesToShow` to the `pages` array.
   */
  getPagesToShow() {
    let pages = [];

    // When we can show all the pages without three dots.
    if (this.maximumPagesToShow >= this.pageCount) {
      // Gets array of number until the page count.
      pages = [...Array(this.pageCount).keys()].map((index) => index + 1);
      this.pagesToShow = pages;
      return;
    }

    const firstPage = 1;
    const lastPage = this.pageCount;
    // We will need always the first page.
    pages.push(firstPage);

    const pageNumberToRight = lastPage - this.activePageNumber;
    const pageNumberToLeft = this.activePageNumber - firstPage;

    // This scenario is when the first page and the last have a distant of more than 3 pages with the active page.
    if (pageNumberToLeft > 3 && pageNumberToRight > 3) {
      pages.push(this.activePageNumber - 1);
      pages.push(this.activePageNumber);
      pages.push(this.activePageNumber + 1);
    }

    // This scenario is when the first page does not have a distant of 3 or more pages with the active page.
    if (pageNumberToLeft <= 3) {
      pages.push(firstPage + 1);
      pages.push(firstPage + 2);
      pages.push(firstPage + 3);
      pages.push(firstPage + 4);
    }

    // This scenario is when the last page does not have a distant of 3 or more pages with the active page.
    if (pageNumberToRight <= 3) {
      pages.push(lastPage - 4);
      pages.push(lastPage - 3);
      pages.push(lastPage - 2);
      pages.push(lastPage - 1);
    }

    // We will need always the last page.
    pages.push(lastPage);

    this.pagesToShow = pages;
  }

  /**
   * Updates the visibility of a pagination bar based on the number of pages to show.
   */
  updatePaginationBarVisibility() {
    if (this.pagesToShow.length > 0) {
      this.container.style.display = 'flex';
    } else {
      this.container.style.display = 'none';
    }
  }

  /**
   * Updates the pagination UI based on the current active page number and the total number of pages.
   */
  build() {
    this.optionList.innerHTML = '';
    this.optionList.dataset.pageCount = this.pageCount;
    this.previousButton.disabled = this.activePageNumber === 1;
    this.nextButton.disabled = this.activePageNumber === this.pageCount;

    this.pagesToShow.forEach((pageNumber, pageIndex) => {
      const previousPageNumber = this.pagesToShow[pageIndex - 1] || 0;
      // We know when to add the three dots because there is a jump on the counting.
      if (previousPageNumber + 1 !== pageNumber) {
        const threeDotsElement = `<li class="nhs-c-pagination__spacer-container nhs-mq-pagination__spacer-container--extra-large">
                                    <span>...</span>
                                  </li>`;
        this.optionList.innerHTML += threeDotsElement;
      }

      const newPaginationNumberLink = new PaginationNumberLink();
      newPaginationNumberLink.setPageNumber(pageNumber);
      newPaginationNumberLink.setUrl(this.urlComponents);
      this.optionList.innerHTML += newPaginationNumberLink.getHtml();
    });
  }
}

export default PaginationBar;
